import React, { createContext, useContext, useEffect, useState } from "react";
import { toast } from "sonner";

const CartContext = createContext(null);
export const useCart = () => useContext(CartContext);

const STORAGE_KEY = "sbr_cart";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (_) {
      return [];
    }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
  }, [items]);

  const add = (product, qty = 1, variant = null) => {
    setItems((prev) => {
      const variant_id = variant?.variant_id || null;
      const key = (p) => p.product_id === product.product_id && (p.variant_id || null) === variant_id;
      const idx = prev.findIndex(key);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + qty };
        return copy;
      }
      // Effective price/image/color from variant when present
      const effPrice = variant?.price ?? product.price;
      const effImage = (variant?.images?.[0]) || product.images?.[0] || "";
      const color = variant?.color || product.color || "";
      return [
        ...prev,
        {
          product_id: product.product_id,
          variant_id,
          color,
          name: product.name,
          price: effPrice,
          image: effImage,
          quantity: qty,
        },
      ];
    });
    toast.success("Added to cart", { description: variant ? `${product.name} · ${variant.color}` : product.name });
    setDrawerOpen(true);
  };

  const remove = (product_id, variant_id = null) =>
    setItems((prev) => prev.filter((i) => !(i.product_id === product_id && (i.variant_id || null) === (variant_id || null))));

  const updateQty = (product_id, quantity, variant_id = null) =>
    setItems((prev) =>
      prev.map((i) => (i.product_id === product_id && (i.variant_id || null) === (variant_id || null) ? { ...i, quantity: Math.max(1, quantity) } : i))
    );

  const clear = () => setItems([]);

  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
  const count = items.reduce((s, i) => s + i.quantity, 0);

  return (
    <CartContext.Provider
      value={{ items, add, remove, updateQty, clear, subtotal, count, drawerOpen, setDrawerOpen }}
    >
      {children}
    </CartContext.Provider>
  );
}
